import { useConversationDrafts } from "@app/components/assistant/conversation/input_bar/useConversationDrafts";
import { isTheme, useTheme } from "@app/components/sparkle/ThemeContext";
import { WorkspacePickerRadioGroup } from "@app/components/WorkspacePicker";
import { useMemoairSupportRoute } from "@app/hooks/useMemoairSupportRoute";
import config from "@app/lib/api/config";
import type { SubscriptionType } from "@app/types/plan";
import type { UserTypeWithWorkspaces, WorkspaceType } from "@app/types/user";
import { datadogLogs } from "@datadog/browser-logs";
import {
  Avatar,
  ButtonsSwitch,
  ButtonsSwitchList,
  ChevronSelectorVertical,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  LogOut01,
  MemoairLogoSquare,
  Monitor01,
  Moon01,
  Settings01,
  Sun,
} from "@dust-tt/sparkle";
import type { RefObject } from "react";

interface UserMenuProps {
  user: UserTypeWithWorkspaces;
  owner: WorkspaceType;
  subscription: SubscriptionType | null;
  adminHref?: string | null;
  adminButtonRef?: RefObject<HTMLDivElement>;
  variant?: "full" | "compact";
}

export function UserMenu({
  user,
  owner,
  subscription,
  adminHref,
  adminButtonRef,
  variant = "full",
}: UserMenuProps) {
  const isCompact = variant === "compact";
  const { clearAllDraftsFromUser } = useConversationDrafts({
    workspaceId: owner.sId,
    userId: user.sId,
    draftKey: "user-menu",
  });

  const hasMultipleWorkspaces = user.workspaces.length > 1;

  const canUseProduct = !!subscription?.plan.limits.canUseProduct;
  const settingsHref =
    adminHref ?? (canUseProduct ? `/w/${owner.sId}/me` : null);

  const { theme, setTheme } = useTheme();

  const supportHref = useMemoairSupportRoute({
    workspaceId: owner.sId,
    disabled: !canUseProduct,
  });

  const handleSignOut = () => {
    clearAllDraftsFromUser();
    datadogLogs.clearUser();
    window.DD_RUM?.onReady(() => {
      window.DD_RUM?.clearUser();
    });
    window.location.href = `${config.getApiBaseUrl()}/api/workos/logout`;
  };

  return (
    <div ref={adminButtonRef} className={isCompact ? undefined : "m-2"}>
      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(
            "group outline-none",
            "transition-[background-color,border-color,box-shadow] duration-150 motion-reduce:transition-none",
            "focus-visible:ring-2 focus-visible:ring-highlight-300",
            isCompact
              ? "rounded-full p-0.5 ring-offset-2 ring-offset-app-background hover:ring-2 hover:ring-border data-[state=open]:ring-2 data-[state=open]:ring-highlight-300"
              : cn(
                  "w-full rounded-xl border border-stone-200 bg-background dark:border-stone-700 text-left shadow-2xs",
                  "hover:border-blue-300/70 hover:shadow-sm dark:hover:border-blue-800/60",
                  "data-[state=open]:border-blue-300/80 data-[state=open]:bg-selected data-[state=open]:shadow-sm dark:data-[state=open]:border-blue-800/60"
                )
          )}
        >
          <span className="sr-only">Open account menu</span>
          {isCompact ? (
            <Avatar
              size="sm"
              visual={user.image}
              name={user.fullName}
              clickable
              isRounded
            />
          ) : (
            <div className="flex min-w-0 items-center gap-2.5 px-2.5 py-2">
              <Avatar
                size="sm"
                visual={user.image}
                name={user.fullName}
                clickable
                isRounded
              />
              <div className="flex min-w-0 flex-1 flex-col items-start">
                <span className="w-full truncate text-sm font-semibold text-foreground">
                  {user.firstName}
                </span>
                <span className="w-full truncate text-xs text-muted-foreground">
                  {owner.name}
                </span>
              </div>
              <Icon
                visual={ChevronSelectorVertical}
                size="xs"
                className="shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
              />
            </div>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side={isCompact ? "right" : "top"}
          align={isCompact ? "end" : "start"}
          sideOffset={isCompact ? 12 : 8}
          className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-64 rounded-2xl border-stone-200 bg-muted-background dark:border-stone-700 p-2 shadow-xl"
        >
          <div className="flex items-center gap-2.5 px-1.5 pb-2.5 pt-1">
            <Avatar
              size="md"
              visual={user.image}
              name={user.fullName}
              isRounded
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-foreground">
                {user.fullName}
              </div>
              {user.email ? (
                <div className="truncate text-xs text-muted-foreground">
                  {user.email}
                </div>
              ) : null}
            </div>
          </div>
          {hasMultipleWorkspaces && (
            <div className="mb-2 overflow-hidden rounded-xl border border-stone-200 bg-background dark:border-stone-700 p-1">
              <DropdownMenuLabel label="Workspace" />
              <WorkspacePickerRadioGroup user={user} workspace={owner} />
            </div>
          )}
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-background dark:border-stone-700 p-1">
            <div className="flex items-center justify-between gap-2 px-2 py-1.5">
              <span className="text-sm text-muted-foreground">Theme</span>
              <ButtonsSwitchList
                size="xs"
                aria-label="Theme"
                value={theme}
                onValueChange={(next) => {
                  if (isTheme(next)) {
                    setTheme(next);
                  }
                }}
              >
                <ButtonsSwitch
                  value="light"
                  tooltip="Light"
                  aria-label="Light"
                  icon={Sun}
                />
                <ButtonsSwitch
                  value="dark"
                  tooltip="Dark"
                  aria-label="Dark"
                  icon={Moon01}
                />
                <ButtonsSwitch
                  value="system"
                  tooltip="System"
                  aria-label="System"
                  icon={Monitor01}
                />
              </ButtonsSwitchList>
            </div>
            <DropdownMenuSeparator />
            {supportHref && (
              <DropdownMenuItem
                label="Talk to us"
                icon={MemoairLogoSquare}
                href={supportHref}
              />
            )}
            {settingsHref && (
              <DropdownMenuItem
                label="Settings"
                icon={Settings01}
                href={settingsHref}
              />
            )}
            {settingsHref && <DropdownMenuSeparator />}
            <DropdownMenuItem
              label="Sign out"
              icon={LogOut01}
              variant="warning"
              onClick={handleSignOut}
            />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
